import React, { useMemo } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { Panel, Tag, Button, FlexboxGrid, Loader, List } from 'rsuite'
import PlayOutlineIcon from '@rsuite/icons/PlayOutline'
import SiteParsingIcon from '@rsuite/icons/SiteParsing'
import SpeakerIcon from '@rsuite/icons/Speaker'
import TagNumberIcon from '@rsuite/icons/TagNumber'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import TimeIcon from '@rsuite/icons/Time'

import type { Banner, Promocode, Labels, Stories } from '@@partner:types:app'
import { useFetchMarketingDashboard } from '@@partner:graphql/query/marketing/marketingDashboard.gql'
import { formatDate } from '@@partner:helpers/util'
import { StatCard } from '@components/common/StatCard'
import { NO_PHOTO_IMAGE } from '@@partner:config/image'

import {
  ROUTE_MARKETING_MANAGEMENT,
  ROUTE_MARKETING_BANNER_ADD,
  ROUTE_MARKETING_STORIES_ADD,
  ROUTE_MARKETING_LABEL_ADD,
  ROUTE_MARKETING_PROMOCODE_ADD,
  ROUTE_MARKETING_BANNER_EDIT,
  ROUTE_MARKETING_STORIES_EDIT,
  ROUTE_MARKETING_LABEL_EDIT,
  ROUTE_MARKETING_PROMOCODE_EDIT
} from '@@partner:config/routes'

import './MarketingDashboard.scss'

type ItemType = 'banner' | 'story' | 'label' | 'promocode'

const typeLabels: Record<ItemType, string> = {
  banner: 'Баннер',
  story: 'История',
  label: 'Лейбл',
  promocode: 'Промокод'
}

const typeColors: Record<ItemType, 'blue' | 'violet' | 'orange' | 'green'> = {
  banner: 'blue',
  story: 'violet',
  label: 'orange',
  promocode: 'green'
}

const getEditLink = (type: ItemType, id: string, brandSlug: string) => {
  switch (type) {
  case 'banner':
    return generatePath(ROUTE_MARKETING_BANNER_EDIT, { brandSlug, bannerId: id })
  case 'story':
    return generatePath(ROUTE_MARKETING_STORIES_EDIT, { brandSlug, storyId: id })
  case 'label':
    return generatePath(ROUTE_MARKETING_LABEL_EDIT, { brandSlug, labelId: id })
  case 'promocode':
    return generatePath(ROUTE_MARKETING_PROMOCODE_EDIT, { brandSlug, promocodeId: id })
  }
}

interface ExpiringItemProps {
  id: string
  name: string
  date: number
  type: ItemType
  brandSlug: string
  imageUrl?: string
}

function ExpiringItem ({ id, name, date, type, brandSlug, imageUrl }: ExpiringItemProps) {
  const daysLeft = Math.ceil((date - Date.now()) / (1000 * 60 * 60 * 24))
  const isUrgent = daysLeft <= 2

  return (
    <List.Item className="MarketingDashboard__ExpiringItem">
      <Link
        to={getEditLink(type, id, brandSlug)}
        target="_blank"
        rel="noopener noreferrer"
        className="MarketingDashboard__ExpiringItemLink"
      >
        <FlexboxGrid justify="space-between" align="middle">
          <FlexboxGrid.Item className="MarketingDashboard__ItemInfo">
            {type !== 'promocode' && (
              <img
                src={imageUrl ?? NO_PHOTO_IMAGE}
                alt={name}
                className="MarketingDashboard__ItemImage"
              />
            )}
            <Tag size="sm" color={typeColors[type]}>{typeLabels[type]}</Tag>
            <span className="MarketingDashboard__ExpiringItemName">{name}</span>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item>
            <Tag color={isUrgent ? 'red' : 'orange'}>
              {daysLeft <= 0 ? 'Сегодня' : `${daysLeft} дн.`}
            </Tag>
            <span className="MarketingDashboard__ExpiringItemDate">
              {formatDate(new Date(date), false)}
            </span>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Link>
    </List.Item>
  )
}

interface UpcomingItemProps {
  id: string
  name: string
  startDate: number
  type: ItemType
  brandSlug: string
  imageUrl?: string
}

function UpcomingItem ({ id, name, startDate, type, brandSlug, imageUrl }: UpcomingItemProps) {
  const daysUntil = Math.ceil((startDate - Date.now()) / (1000 * 60 * 60 * 24))

  return (
    <List.Item className="MarketingDashboard__UpcomingItem">
      <Link
        to={getEditLink(type, id, brandSlug)}
        target="_blank"
        rel="noopener noreferrer"
        className="MarketingDashboard__UpcomingItemLink"
      >
        <FlexboxGrid justify="space-between" align="middle">
          <FlexboxGrid.Item className="MarketingDashboard__ItemInfo">
            {type !== 'promocode' && (
              <img
                src={imageUrl ?? NO_PHOTO_IMAGE}
                alt={name}
                className="MarketingDashboard__ItemImage"
              />
            )}
            <Tag size="sm" color={typeColors[type]}>{typeLabels[type]}</Tag>
            <span className="MarketingDashboard__UpcomingItemName">{name}</span>
          </FlexboxGrid.Item>
          <FlexboxGrid.Item>
            <Tag color="blue">
              {daysUntil <= 0 ? 'Сегодня' : daysUntil === 1 ? 'Завтра' : `через ${daysUntil} дн.`}
            </Tag>
            <span className="MarketingDashboard__UpcomingItemDate">
              {formatDate(new Date(startDate), false)}
            </span>
          </FlexboxGrid.Item>
        </FlexboxGrid>
      </Link>
    </List.Item>
  )
}

export default function MarketingDashboard () {
  const { brandSlug } = useParams<{ brandSlug: string }>()
  const { data, loading, error } = useFetchMarketingDashboard()

  const toTimestamp = (date: Date | number | undefined): number | null => {
    if (!date) return null
    if (typeof date === 'number') return date
    return new Date(date).getTime()
  }

  const expiringItems = useMemo(() => {
    if (!data) return []

    const items: { id: string; name: string; date: number; type: ItemType; imageUrl?: string }[] = []

    data.expiringBanners?.edges?.forEach((b: Banner) => {
      const ts = toTimestamp(b.activeUntil)
      if (ts) {
        items.push({
          id: String(b.id),
          name: b.name,
          date: ts,
          type: 'banner',
          imageUrl: b.bannerSlide?.url
        })
      }
    })

    data.expiringStories?.edges?.forEach((s: Stories) => {
      const ts = toTimestamp(s.activeUntil)
      if (ts && s.id) {
        items.push({
          id: String(s.id),
          name: s.name ?? 'История',
          date: ts,
          type: 'story',
          imageUrl: s.preview?.url
        })
      }
    })

    data.expiringLabels?.edges?.forEach((l: Labels) => {
      const ts = toTimestamp(l.activeUntil)
      if (ts && l.id) {
        items.push({
          id: String(l.id),
          name: l.name,
          date: ts,
          type: 'label',
          imageUrl: l.image?.url
        })
      }
    })

    data.expiringPromocodes?.edges?.forEach((p: Promocode) => {
      const ts = toTimestamp(p.active_until)
      if (ts) {
        items.push({ id: String(p.id), name: p.code, date: ts, type: 'promocode' })
      }
    })

    // Сортируем по дате истечения
    return items.sort((a, b) => a.date - b.date)
  }, [ data ])

  const upcomingItems = useMemo(() => {
    if (!data) return []

    const items: { id: string; name: string; startDate: number; type: ItemType; imageUrl?: string }[] = []

    data.upcomingBanners?.edges?.forEach((b: Banner) => {
      const ts = toTimestamp(b.activeFrom)
      if (ts) {
        items.push({
          id: String(b.id),
          name: b.name,
          startDate: ts,
          type: 'banner',
          imageUrl: b.bannerSlide?.url
        })
      }
    })

    data.upcomingStories?.edges?.forEach((s: Stories) => {
      const ts = toTimestamp(s.activeFrom)
      if (ts && s.id) {
        items.push({
          id: String(s.id),
          name: s.name ?? 'История',
          startDate: ts,
          type: 'story',
          imageUrl: s.preview?.url
        })
      }
    })

    data.upcomingLabels?.edges?.forEach((l: Labels) => {
      const ts = toTimestamp(l.activeFrom)
      if (ts && l.id) {
        items.push({
          id: String(l.id),
          name: l.name,
          startDate: ts,
          type: 'label',
          imageUrl: l.image?.url
        })
      }
    })

    data.upcomingPromocodes?.edges?.forEach((p: Promocode) => {
      const ts = toTimestamp(p.active_from)
      if (ts) {
        items.push({ id: String(p.id), name: p.code, startDate: ts, type: 'promocode' })
      }
    })

    // Сортируем по дате начала (ближайшие первыми)
    return items.sort((a, b) => a.startDate - b.startDate)
  }, [ data ])

  const totalExpiring = expiringItems.length
  const totalUpcoming = upcomingItems.length

  if (loading && !data) {
    return (
      <div className="MarketingDashboard MarketingDashboard--loading">
        <Loader size="lg" content="Загрузка..." />
      </div>
    )
  }

  if (error) {
    return (
      <Panel className="MarketingDashboard" shaded>
        <p>Ошибка загрузки данных: {error.message}</p>
      </Panel>
    )
  }

  return (
    <div className="MarketingDashboard">
      {/* Статистика */}
      <div className="MarketingDashboard__Stats">
        <StatCard
          title="Активных баннеров"
          count={data?.activeBanners?.pageInfo?.total ?? 0}
          icon={<SiteParsingIcon />}
          color="#3498db"
          link={generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug, type: 'banners' })}
        />
        <StatCard
          title="Активных историй"
          count={data?.activeStories?.pageInfo?.total ?? 0}
          icon={<PlayOutlineIcon />}
          color="#9b59b6"
          link={generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug, type: 'stories' })}
        />
        <StatCard
          title="Активных лейблов"
          count={data?.activeLabels?.pageInfo?.total ?? 0}
          icon={<SpeakerIcon />}
          color="#e67e22"
          link={generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug, type: 'labels' })}
        />
        <StatCard
          title="Активных промокодов"
          count={data?.activePromocodes?.pageInfo?.total ?? 0}
          icon={<TagNumberIcon />}
          color="#27ae60"
          link={generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug, type: 'promocodes' })}
        />
      </div>

      <div className="MarketingDashboard__Content">
        {/* Истекающие и будущие элементы */}
        <div className="MarketingDashboard__LeftColumn">
          <Panel
            className="MarketingDashboard__Panel"
            header={
              <FlexboxGrid justify="space-between" align="middle">
                <span>
                  {totalExpiring > 0 ? (
                    <>Истекает в ближайшие 7 дней</>
                  ) : (
                    <><CheckRoundIcon color="#27ae60" /> Нет истекающих элементов</>
                  )}
                </span>
                {totalExpiring > 0 && (
                  <Tag color="orange">{totalExpiring}</Tag>
                )}
              </FlexboxGrid>
            }
            shaded
            bordered
          >
            {expiringItems.length > 0 ? (
              <List hover>
                {expiringItems.slice(0, 10).map((item) => (
                  <ExpiringItem
                    key={`${item.type}-${item.id}`}
                    id={item.id}
                    name={item.name}
                    date={item.date}
                    type={item.type}
                    brandSlug={brandSlug!}
                    imageUrl={item.imageUrl}
                  />
                ))}
              </List>
            ) : (
              <p className="MarketingDashboard__EmptyMessage">
                Все элементы в порядке! Нет контента с истекающим сроком действия.
              </p>
            )}
          </Panel>

          {/* Будущие элементы */}
          <Panel
            className="MarketingDashboard__Panel"
            header={
              <FlexboxGrid justify="space-between" align="middle">
                <span>
                  {totalUpcoming > 0 ? (
                    <><TimeIcon color="#3498db" /> Запланировано на будущее</>
                  ) : (
                    <>Нет запланированных элементов</>
                  )}
                </span>
                {totalUpcoming > 0 && (
                  <Tag color="blue">{totalUpcoming}</Tag>
                )}
              </FlexboxGrid>
            }
            shaded
            bordered
          >
            {upcomingItems.length > 0 ? (
              <List hover>
                {upcomingItems.slice(0, 10).map((item) => (
                  <UpcomingItem
                    key={`upcoming-${item.type}-${item.id}`}
                    id={item.id}
                    name={item.name}
                    startDate={item.startDate}
                    type={item.type}
                    brandSlug={brandSlug!}
                    imageUrl={item.imageUrl}
                  />
                ))}
              </List>
            ) : (
              <p className="MarketingDashboard__EmptyMessage">
                Нет запланированных на будущее элементов.
              </p>
            )}
          </Panel>
        </div>

        {/* Топ промокодов и быстрые действия */}
        <div className="MarketingDashboard__RightColumn">
          <Panel
            className="MarketingDashboard__Panel"
            header="Топ промокодов по активациям"
            shaded
            bordered
          >
            {data?.topPromocodes?.edges?.length ? (
              <List hover>
                {data.topPromocodes.edges.map((promo: Promocode) => (
                  <List.Item key={promo.id} className="MarketingDashboard__PromoItem">
                    <FlexboxGrid justify="space-between" align="middle">
                      <FlexboxGrid.Item>
                        <Tag color="blue">{promo.code}</Tag>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item>
                        <Tag color="green">
                          {promo.activated_count}
                          {promo.max_total_activation_count !== -1 && (
                            <> / {promo.max_total_activation_count}</>
                          )}
                        </Tag>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  </List.Item>
                ))}
              </List>
            ) : (
              <p className="MarketingDashboard__EmptyMessage">Нет активных промокодов</p>
            )}
          </Panel>

          <Panel
            className="MarketingDashboard__Panel MarketingDashboard__QuickActions"
            header="Быстрые действия"
            shaded
            bordered
          >
            <FlexboxGrid justify="start" style={{ gap: 8 }}>
              <Button
                appearance="primary"
                color="blue"
                as={Link}
                to={generatePath(ROUTE_MARKETING_BANNER_ADD, { brandSlug })}
                size="sm"
              >
                + Баннер
              </Button>
              <Button
                appearance="primary"
                color="violet"
                as={Link}
                to={generatePath(ROUTE_MARKETING_STORIES_ADD, { brandSlug })}
                size="sm"
              >
                + История
              </Button>
              <Button
                appearance="primary"
                color="orange"
                as={Link}
                to={generatePath(ROUTE_MARKETING_LABEL_ADD, { brandSlug })}
                size="sm"
              >
                + Лейбл
              </Button>
              <Button
                appearance="primary"
                color="green"
                as={Link}
                to={generatePath(ROUTE_MARKETING_PROMOCODE_ADD, { brandSlug })}
                size="sm"
              >
                + Промокод
              </Button>
            </FlexboxGrid>
          </Panel>
        </div>
      </div>
    </div>
  )
}
